/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import { trimPrefix } from '../string/string-util';
import { parseURL } from '../url';

/**
 * @import { UpdateUrlConfigParser } from '.'
 * @import { Logger } from '../logger/logger'
 */

/**
 * @typedef {string} UrlHashParams String ne nepodobný serializaci {@link URLSearchParams.toString}
 * (bez `#` ), akorát pro {@link URL.hash}. Prázdný řetězec reprezentuje
 * (bohužel nerozlišitelně) zadané prázdné nebo nezadané informace.
 * @typedef {UrlHashParams} Info
 */

/**
 * @param {string} url
 * @returns {Info}
 * @throws {Error} Pokud URL není validní.
 */
export function getUpdateUrlOpaqueInfo(url) {
  const u = parseURL(url);
  return trimPrefix(u.hash, '#');
}

/**
 * @param {string} url
 * @param {Info} info
 * @returns {string} Vrátí URL s opaque info.
 * @throws {Error} Pokud URL není validní.
 */
export function setUpdateUrlOpaqueInfo(url, info) {
  const u = parseURL(url);
  if (info === '') {
    return u.href;
  }
  u.hash = `#${info}`;
  return u.href;
}

/**
 * @param {string} url
 * @param {Info | undefined} info
 * @param {Logger} logger
 * @returns {string} Vrátí URL s opaque info.
 */
export function mustSetUpdateUrlOpaqueInfo(url, info, logger) {
  try {
    if (info !== undefined) {
      return setUpdateUrlOpaqueInfo(url, info);
    }
  } catch (err) {
    logger.error(err);
  }
  return url;
}

/** @type {UpdateUrlConfigParser<Info>['parseUpdateUrlConfig']} */
export function parseUpdateUrlConfig(info) {
  // URLSearchParams dělá svévolný url decoding. Dělá to z důvodu, že jsou jiná
  // pravidla pro escaping v search params a jiná v hash.
  const p = new URLSearchParams(info);
  const contentSettings = p.get('contentSettings') ?? undefined;
  const deviceId = getStringOrEmptyStringAsNull(p, 'd');
  const fingerprintStrategy = p.get('f') ?? undefined;
  const serverUrl = getStringOrEmptyStringAsNull(p, 's');
  const preview = p.get('p') ?? undefined;
  return { contentSettings, deviceId, fingerprintStrategy, preview, serverUrl };
}

/**
 * Získá první výskyt zadaného parametru. Pokud parametr neexistuje
 * je vráceno undefined. Pokud je parametr '', tak je vráceno null.
 * @param {URLSearchParams} params
 * @param {string} key
 * @returns {string | null | undefined}
 */
function getStringOrEmptyStringAsNull(params, key) {
  const value = params.get(key) ?? undefined;
  if (value === '') {
    return null;
  }
  return value;
}
